import React from 'react';
import SidePanel from '@ajat/design-system/SidePanel';
import { useCart } from '../../../../context/CartContext';

/**
 * Shared "Order Details" side panel for all brand checkout pages.
 * Renders live cart contents (products, packages, totals, discount) instead
 * of static markup. Sections and zero rows are hidden when empty.
 *
 * @param {Object} props
 * @param {boolean}  props.isOpen
 * @param {Function} props.onClose
 * @param {string}   props.brandSlug    - Cart brand slug ('clsdk' | 'clsse' | 'clsfi' | 'abcse')
 * @param {string}   props.priceSuffix  - Price suffix (',-' | ':-' | ' €')
 * @param {string}   props.title        - Panel title (localized)
 * @param {Object}   props.labels       - { products, packages, discount, total }
 * @param {number}   [props.fee]        - Selected payment fee; row rendered only when > 0
 * @param {string}   [props.feeLabel]   - Fee row label (e.g. 'Fakturagebyr')
 * @param {string}   props.totalDisplay - Pre-formatted total (already includes fee)
 */
const OrderDetailsPanel = ({
  isOpen,
  onClose,
  brandSlug,
  priceSuffix,
  title,
  labels,
  fee = 0,
  feeLabel,
  totalDisplay,
}) => {
  const { products, packages, productsTotal, packagesTotal, discountAmount } = useCart(brandSlug);

  const fmt = (v) => `${Math.round(Number(v) || 0)}${priceSuffix}`;

  const renderSection = (heading, items) => items.length > 0 && (
    <div className="checkout__details-section">
      <h5 className="checkout__details-heading typo-h5">{heading}</h5>
      <div className="checkout__details-list">
        {items.map((item) => (
          <div className="checkout__details-item" key={item.id}>
            <div className="checkout__details-item-info">
              <span className="typo-body">{item.name}</span>
              <span className="checkout__details-item-qty typo-body-sm">
                {item.quantity} × {fmt(item.unitPrice)}
              </span>
            </div>
            <span className="typo-body">{fmt(item.unitPrice * item.quantity)}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title={title}>
      <div className="checkout__details">
        {renderSection(labels.products, products)}
        {renderSection(labels.packages, packages)}

        <div className="checkout__details-summary">
          {products.length > 0 && (
            <div className="checkout__details-row">
              <span className="typo-body">{labels.products}</span>
              <span className="typo-body">{fmt(productsTotal)}</span>
            </div>
          )}
          {packages.length > 0 && (
            <div className="checkout__details-row">
              <span className="typo-body">{labels.packages}</span>
              <span className="typo-body">{fmt(packagesTotal)}</span>
            </div>
          )}
          {discountAmount > 0 && (
            <div className="checkout__details-row checkout__details-row--discount">
              <span className="typo-body">{labels.discount}</span>
              <span className="typo-body">-{fmt(discountAmount)}</span>
            </div>
          )}
          {fee > 0 && (
            <div className="checkout__details-row">
              <span className="typo-body">{feeLabel}</span>
              <span className="typo-body">{fmt(fee)}</span>
            </div>
          )}
          <div className="checkout__details-divider" />
          <div className="checkout__details-row checkout__details-row--total">
            <span className="typo-h3">{labels.total}</span>
            <span className="typo-h3">{totalDisplay}</span>
          </div>
        </div>
      </div>
    </SidePanel>
  );
};

export default OrderDetailsPanel;
